import { Box, Typography } from '@material-ui/core';
import { useEffect, useMemo, useState, useRef, useCallback } from 'react';
import React from 'reactn';
import Hoverable from '../../../hoverable';
import { VoiceCallService } from '../../services/voice-call.service';
import moment from 'moment-timezone';
import { VOICE_CALL } from '../../constants/voice-call-constants';
import { LOCATIONS } from '../../../../constants';
import { columnSortByOrderDirection } from '../../../../util/Helpers';
import { getCallStatusComponents, getSelectedLocationTimeZone, getVoiceInsights } from '../../utils/utils';
import VoiceInsightChip from '../voice-insight-chip/voice-insight-chip';
import { Button } from '@orijinworks/frontend-commons';
import { ViewDetailStyles } from './call-history.component.style';
import useUnleashHook from '../../../../v2/core/unleash/useUnleashHook';
import { UnleashService } from '../../../../v2/services';

export const useCallHistoryListComponentHook = (props, isKeywordFlagEnabled) => {
  const isV2CallHistoryFlagEnabled = useUnleashHook(UnleashService.FLAGS.V2_CALL_HISTORY).isFlagEnabled();
  const [defaultPageSize, setDefaultPageSize] = useState(10);
  const voiceCallService = new VoiceCallService();
  const [callsData, setCallsData] = useState([]);
  const [isLoading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [isDetailModalOpen, setDetailModalState] = useState(false);
  const [callDetails, setCallDetails] = useState({});
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [callStatus] = useState('');
  const [transcriptData, setTranscriptData] = useState([]);
  const [detectedKeywords, setDetectedKeywords] = useState([]);
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarAutoHideDuration, setSnackbarAutoHideDuration] = useState(3000);
  const [isSuccessSnackbar, setIsSuccessSnackbar] = useState(true);
  const tableRef = React.useRef();
  const [isPrivileged, setIsPrivileged] = useState(false);
  const [transcriptSummary, setTranscriptSummary] = useState({
    main_topics: '',
    inappropriate_violent_themes: [],
  });
  const [stateFilters] = useState({
    learnerFirstName: '',
    learnerDocId: '',
    relationshipType: '',
    contactPhoneNumber: '',
    contactName: '',
    endTimeUtc: '',
    callState: [],
    voiceCallInsights: [],
  });
  const [allowedFilters] = useState([
    'learnerFirstName',
    'learnerDocId',
    'relationshipType',
    'contactPhoneNumber',
    'contactName',
    'endTimeUtc',
    'callState',
    'voiceCallInsights',
  ]);
  // const [queryForExport, setQueryForExport] = useState([]); // Reserved for future use
  const [page, setPage] = useState(1);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [columns] = useState([
    {
      name: 'LearnerCell',
      preset: 'LearnerCell',
      filter: {
        type: 'LearnerCell'
      },
      displayName: 'Resident',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'preDefined',
      sortByProp: 'learnerUserName'
    },
    {
      name: 'contactName',
      displayName: 'Contact Name',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'value',
      sortByProp: 'contactName',
    },
    {
      name: 'contactPhoneNumber',
      displayName: 'Phone Number',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'value',
      sortByProp: 'contactPhoneNumber',
    },
    {
      name: 'relationshipType',
      preset: 'MultiTagCell',
      displayName: 'Relationship',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'value',
      sortByProp: 'relationshipType',
      filter: {
        type: 'MultiTagCell',
        showBlankFilter: false
      },
      options: VOICE_CALL.getRelationshipTypes()
        .sort((a, b) => a.label.localeCompare(b.label))
        .map(relationship => relationship.label),
    },
    {
      name: 'callState',
      preset: 'IconAndTextCell',
      displayName: 'Call State',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: false,
      isVisible: true,
      type: 'preDefined',
      sortByProp: 'status',
      filter: {
        type: 'MultiTagCell',
        showBlankFilter: false
      },
      options: Object.values(VOICE_CALL.CALL_HISTORY_STATUS)
        .map(status => status.label),
    },
    {
      name: 'endTimeUtc',
      displayName: 'End Time',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: true,
      isVisible: true,
      type: 'value',
      sortByProp: 'endTimeUtc',
      filter: {
        dateFormat: 'yyyy-MM-dd',
        type: 'date-range',
        monthsShown: 2
      }
    },
    {
      name: 'voiceCallInsights',
      preset: 'MultiTagCell',
      displayName: 'Call Insights',
      sortOrder: 'none',
      isFilterable: true,
      isSortable: false,
      isVisible: true,
      type: 'preDefined',
      sortByProp: 'tags',
      filter: {
        type: 'MultiTagCell',
        showBlankFilter: true
      },
      options: Object.values(VOICE_CALL.VOICE_INSIGHTS)
        .filter(({ value }) => value !== VOICE_CALL.VOICE_INSIGHTS.VOICE_MATCH_RESIDENT.value)
        .map(({ label }) => label)
    },
    {
      name: 'action',
      displayName: 'Action',
      sortOrder: 'none',
      isFilterable: false,
      isSortable: false,
      isVisible: true,
      type: 'override',
    },
  ]);
  const [callHistoryData, setCallHistoryData] = useState([]);
  const [callHistoryDatasetProps, setCallHistoryDatasetProps] = useState({
    filter: {
      learnerFirstName: "",
      learnerLastName: "",
      learnerDocId: "",
      relationshipType: "",
      contactPhoneNumber: "",
      firstName: "",
      lastName: "",
      voiceCallInsights: ""
    },
    sort: {
      columnName: "endTimeUtc",
      direction: "desc"
    }
  });

  // Track if component has mounted to prevent duplicate API calls
  const isFirstMount = useRef(true);
  const prevLocationId = useRef(props.locationId);

  // Memoize filter and sort to prevent unnecessary re-renders
  const filterString = useMemo(() => JSON.stringify(callHistoryDatasetProps.filter), [callHistoryDatasetProps.filter]);
  const sortString = useMemo(() => JSON.stringify(callHistoryDatasetProps.sort), [callHistoryDatasetProps.sort]);

  // V1-specific effect for isDataRefresh
  useEffect(() => {
    if (props.isDataRefresh) {
      if (isV2CallHistoryFlagEnabled) {
        getCallHistoryData();
      } else {
        refreshTable();
      }
      if (props.setDataRefresh) {
        props.setDataRefresh(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.isDataRefresh]);

  // V1/V2 hybrid effect for locationId changes
  useEffect(() => {
    if (isV2CallHistoryFlagEnabled) {
      // V2 behavior - handled by main effect
      return;
    }
    // V1 behavior - refresh table on location change
    refreshTable();
    if (props.setShouldExportCallHistory) {
      props.setShouldExportCallHistory(false);
    }
    if (props.setDataRefresh) {
      props.setDataRefresh(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.locationId]);

  const refreshTable = () => {
    tableRef && tableRef.current && tableRef.current.onQueryChange();
  };

  // Helper functions
  const handleSnackbar = (message, success, duration) => {
    setSnackbarMessage(message);
    setOpenSnackbar(true);
    setSnackbarAutoHideDuration(duration);
    setIsSuccessSnackbar(success);
  };

  // Create lookup maps for converting labels back to values
  const relationshipLabelToValue = new Map(
    VOICE_CALL.getRelationshipTypes().map(rel => [rel.label, rel.backendValue])
  );
  const relationshipValueToLabel = new Map(
    VOICE_CALL.getRelationshipTypes().map(rel => [rel.backendValue, rel.label])
  );
  const callStateLabelToValue = new Map(
    Object.values(VOICE_CALL.CALL_HISTORY_STATUS).map(status => [status.label, status.value])
  );
  const callInsightLabelToValue = new Map(
    Object.values(VOICE_CALL.VOICE_INSIGHTS).map(insight => [insight.label, insight.value])
  );

  /**
   * 
   * Parses the JSON & returns the value. Return empty string if NO value is found
   * @param {string} contactInfo in JSON Stringified form
   * @return string 
   */
  const extractContactInfo = (contactInfo, property) => {
    if (contactInfo) {
      const parsedData = JSON.parse(contactInfo);
      return parsedData[property] || '';
    }
    return '';
  };

  /**
   * @name getLocationName
   * @param {number} locationId
   * @desc Based upon locationId it finds in LOCATIONS collection
   * for matching location and shows locationName instead.
   * @return {string}
   */
  const getLocationName = (locationId) => {
    const location = LOCATIONS.find((location) => location.locationId === locationId);
    if (location) {
      return location.locationName;
    } else {
      return '';
    }
  };

  const getColumnValue = (item, column) => {
    let contactInfo = null;
    try {
      contactInfo = item.contactInfo ? JSON.parse(item.contactInfo) : null;
    } catch (error) {
      console.error('Error parsing contactInfo:', error);
    }

    switch (column.title || column.displayName) {
      case 'Resident':
      case 'Resident Name':
        return `${item.learnerFirstName || ''} ${item.learnerLastName || ''}`.trim();
      case 'DOC ID':
        return item.learnerDocId || '';
      case 'Phone Number':
        return item.contactPhoneNumber || '';
      case 'Contact Name':
        return contactInfo ? `${contactInfo.firstName || ''} ${contactInfo.lastName || ''}`.trim() : '';
      case 'Relationship':
        // V2: relationshipType is already formatted in item.relationshipType
        // V1: Get relationship type from contactInfo
        if (item.relationshipType) {
          return item.relationshipType;
        }
        return contactInfo ? contactInfo.relationshipType || '' : '';
      case 'Call End Time':
        return item.endTimeUtc || '';
      case 'Call Status':
        // Return formatted call state using label
        const statusObj = Object.values(VOICE_CALL.CALL_HISTORY_STATUS).find(s => s.value === item.callState);
        return statusObj ? statusObj.label : item.callState;
      case 'Call Insights':
        if (!item.voiceCallInsights || item.voiceCallInsights.length === 0 || (item.callState && (item.callState === VOICE_CALL.CALL_HISTORY_STATUS.CANCELLED.value || item.callState === VOICE_CALL.CALL_HISTORY_STATUS.MISSED.value))) {
          return '';
        }
        // Get unique insights - labels are already in proper case
        const uniqueInsights = [...new Set(item.voiceCallInsights
          .map(event => event.eventType)
          .filter(eventType =>
            VOICE_CALL.VOICE_INSIGHTS &&
            VOICE_CALL.VOICE_INSIGHTS[eventType] &&
            (!VOICE_CALL.VOICE_INSIGHTS.VOICE_MATCH_RESIDENT ||
              VOICE_CALL.VOICE_INSIGHTS[eventType].value !== VOICE_CALL.VOICE_INSIGHTS.VOICE_MATCH_RESIDENT.value)
          )
          .map(eventType => VOICE_CALL.VOICE_INSIGHTS[eventType].label || eventType)
        )]
          ;
        return uniqueInsights.join(', ');
      case 'Keywords':
        return item.keywords ? item.keywords.join(', ') : '';
      case 'End Time':
        // V2 column name
        return item.endTimeUtc || '';
      case 'Call State':
        // V2 column name - Return formatted call state using label
        const statusObj2 = Object.values(VOICE_CALL.CALL_HISTORY_STATUS).find(s => s.value === item.callState);
        return statusObj2 ? statusObj2.label : item.callState;
      default:
        return item[column.field] || '';
    }
  };

  const generateCsvContent = (content) => {
    const exemptedColumnsList = ['Actions', 'Action'];
    const csvColumns = [
      ...columns.filter((column) => !exemptedColumnsList.includes(column.title || column.displayName)),
      {
        title: 'Keywords',
        field: 'keywords'
      }
    ];

    const columnTitles = csvColumns
      .map((column) => column.title || column.displayName)
      .join(',');

    let csvContent = columnTitles + '\r\n';
    const domParser = new DOMParser();

    content.forEach((item) => {
      const rowValues = csvColumns.map((column) => {
        let value = getColumnValue(item.rowData, column);
        const document = domParser.parseFromString(value, 'text/html');
        return '"' + document.body.innerText + '"';
      });
      const rowString = rowValues.join(',');
      csvContent += rowString + '\r\n';
    });

    return csvContent;
  };

  const generateCsv = (csvContent) => {
    let locationName = getLocationName(props.locationId);
    const exportedFilename = `${locationName} Calls History.csv`;
    const universalBOM = '\uFEFF';
    const downloadLink = document.createElement('a');
    downloadLink.download = exportedFilename;
    downloadLink.href = `data:text/csv;charset=utf-8,${encodeURIComponent(universalBOM + csvContent)}`;
    downloadLink.click();
  };

  const handleExportCsv = useCallback(async () => {
    try {
      const content = callHistoryData;
      if (!content || content.length === 0) {
        handleSnackbar('No data to export', false, 2000);
        return;
      }
      const csvContent = generateCsvContent(content);
      if (csvContent) {
        generateCsv(csvContent);
        handleSnackbar('CSV file has been exported successfully', true, 2000);
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      handleSnackbar('Failed to export CSV', false, 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callHistoryData, columns, props.locationId]);

  const handleExportCsvV1 = async () => {
    try {
      // V1: Get current visible data from MaterialTable's internal state
      let content = [];

      if (tableRef && tableRef.current && tableRef.current.state && tableRef.current.state.data) {
        content = tableRef.current.state.data;
      } else {
        // Fallback to callHistoryData if tableRef not available
        content = callHistoryData;
      }

      if (!content || content.length === 0) {
        handleSnackbar('No data to export', false, 2000);
        return;
      }
      // tableColumns is accessed from the closure
      const csvContent = generateCsvContentV1(tableColumns, content);
      if (csvContent) {
        generateCsv(csvContent);
        handleSnackbar('CSV file has been exported successfully', true, 2000);
      }
    } catch (error) {
      console.error('Error exporting CSV:', error);
      handleSnackbar('Failed to export CSV', false, 2000);
    }
  };

  const generateCsvContentV1 = (tableColumnsRef, content) => {
    const exemptedColumnsList = ['Actions', 'Action'];
    const csvColumns = [
      ...(tableColumnsRef || []).filter((column) => !exemptedColumnsList.includes(column.title || column.displayName)),
      {
        title: 'Keywords',
        field: 'keywords'
      }
    ];

    const columnTitles = csvColumns
      .map((column) => column.title || column.displayName)
      .join(',');

    let csvContent = columnTitles + '\r\n';
    const domParser = new DOMParser();

    content.forEach((item) => {
      const rowValues = csvColumns.map((column) => {
        if (column.field) {
          // Handle both V2 format (item.rowData) and raw format (item)
          const dataItem = item.rowData || item;
          let value = getColumnValue(dataItem, column);
          const document = domParser.parseFromString(value, 'text/html');
          return '"' + document.body.innerText + '"';
        }
        return '';
      });
      const rowString = rowValues.join(',');
      csvContent += rowString + '\r\n';
    });

    return csvContent;
  };

  const mapToCallHistory = (data, onDetailsClickFn) => data.map((item) => {
    const relationshipType = extractContactInfo(item.contactInfo, 'relationshipType');
    const formattedRelationshipType = relationshipValueToLabel.get(relationshipType.toUpperCase()) || relationshipType;

    return {
      isSelected: false,
      rowData: {
        id: item.meetingId,
        contactName: `${extractContactInfo(item.contactInfo, 'firstName')} ${extractContactInfo(item.contactInfo, 'lastName')}`,
        contactPhoneNumber: item.contactPhoneNumber,
        relationshipType: formattedRelationshipType,
        callType: item.callType,
        callState: item.callState, // Keep original for filtering
        endTimeUtc: item.endTimeUtc ? moment.utc(item.endTimeUtc).tz(getSelectedLocationTimeZone()).format('MMM Do YYYY h:mm a') : '',
        voiceCallInsights: item.voiceCallInsights,
        learnerUserName: item.learnerUserName,
        learnerFirstName: item.learnerFirstName,
        learnerLastName: item.learnerLastName,
        learnerDocId: item.learnerDocId,
        keywords: item.keywords,
        contactInfo: item.contactInfo,
        action: () => (
          <>
            {[VOICE_CALL.CALL_HISTORY_STATUS.COMPLETED.value, VOICE_CALL.CALL_HISTORY_STATUS.TERMINATED.value].includes(item.callState) && (
              <Button
                onClick={() => onDetailsClickFn(item)}
                sx={ViewDetailStyles}>
                View Details
              </Button>
            )}
          </>
        ),
      },
      preDefineComponentProps: {
        voiceCallInsights: {
          tags: getVoiceInsights(item.voiceCallInsights, item.callState).map((voiceInsight) => ({
            label: VOICE_CALL.VOICE_INSIGHTS[voiceInsight.eventType]?.label,
            color: 'warning',
            size: 'small',
            variant: 'filled',
            sx: {
              backgroundColor: item.callType === 'VRS' ? '#FFF7E3' : '#FFECF0',
              color: item.callType === 'VRS' ? 'var(--yellow-deep)' : '#7C0E22',
              border: item.callType === 'VRS' ? '1px solid var(--yellow-main)' : '1px solid #E32D4E',
            },
          }))
        },
        LearnerCell: {
          username: item.learnerUserName,
          firstName: item.learnerFirstName,
          lastName: item.learnerLastName,
          docId: item.learnerDocId,
        },
        callState: getCallStatusComponents(item.callState)
      }
    };
  });

  const getCallHistoryData = useCallback(async () => {
    try {
      setLoading(true);
      let callStatus = Object.values(VOICE_CALL.CALL_HISTORY_STATUS).map(s => s.value).join().toLowerCase();
      if (callHistoryDatasetProps.filter.hasOwnProperty("callState")) {
        callStatus = callHistoryDatasetProps.filter.callState
          .toLowerCase()
          .split(",")
          .map(item => item.trim().replaceAll('%', ''))
          .join(",");
      }
      const liveCallData = await voiceCallService.getLiveCallsList({
        locationId: props.locationId,
        status: callStatus,
        pageNumber: page - 1,
        size: defaultPageSize,
        sortBy: callHistoryDatasetProps.sort.columnName,
        sortOrder: callHistoryDatasetProps.sort.direction,
        tbody: callHistoryDatasetProps.filter,
      });

      // onDetailsClick will be available when this function is called (from closure)
      // eslint-disable-next-line no-use-before-define
      const transformedData = mapToCallHistory(liveCallData.content, onDetailsClick);
      setTotalCount(liveCallData.totalElements);
      setCallHistoryData(transformedData);
      setLastRefreshed(new Date());
    } catch (e) {
      handleSnackbar("Something went wrong, please reload the page!", "error", 2000);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.locationId, page, defaultPageSize, callHistoryDatasetProps.filter, callHistoryDatasetProps.sort]);

  // Single effect to handle all data fetching
  useEffect(() => {
    if (!props.locationId) return;

    const locationIdChanged = prevLocationId.current !== props.locationId;

    // On first mount or locationId change, reset first mount flag
    if (isFirstMount.current || locationIdChanged) {
      isFirstMount.current = false;
      prevLocationId.current = props.locationId;
    }

    // Skip the first run of page/filter/sort changes (they'll be handled by locationId change on mount)
    if (isFirstMount.current) {
      return;
    }

    getCallHistoryData();
  }, [props.locationId, page, defaultPageSize, filterString, sortString, getCallHistoryData]);

  useEffect(() => {
    const { shouldExportCallHistory, setShouldExportCallHistory } = props;
    if (shouldExportCallHistory && totalCount) {
      if (isV2CallHistoryFlagEnabled) {
        // V2: use handleExportCsv
        handleExportCsv();
      } else {
        // V1: handleExportCsvV1 accesses tableColumns from closure
        handleExportCsvV1();
      }
      setShouldExportCallHistory(false);
    }
    if (shouldExportCallHistory && !totalCount) {
      handleSnackbar('No data to export', false, 2000);
      if (setShouldExportCallHistory) {
        setShouldExportCallHistory(false);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.shouldExportCallHistory, props.setShouldExportCallHistory, totalCount, handleExportCsv, isV2CallHistoryFlagEnabled]);
  const handleCloseHistoryDetailsModal = () => {
    setDetailModalState(false);
  };
  const onDetailsClick = async (callHistoryData) => {
    setRecordingUrl('');
    setTranscriptData([]);
    setTranscriptSummary({
      main_topics: '',
      inappropriate_violent_themes: [],
    });

    const { meetingId } = callHistoryData;
    setCallDetails(callHistoryData);
    setDetailModalState(true);
    const { preSignedURLAudioRecording, preSignedURLTranscript, privileged, keywords } =
      await voiceCallService.getVoiceCallDetails(meetingId);
    setDetectedKeywords(keywords);
    if (privileged) {
      setIsPrivileged(true);
    } else {
      setIsPrivileged(false);
      setRecordingUrl(preSignedURLAudioRecording);
      getTranscriptData(preSignedURLTranscript);
    }
  };
  const getTranscriptData = async (preSignedURLTranscript) => {
    const transcriptData = await voiceCallService.getVoiceCallTranscription(preSignedURLTranscript);
    if (transcriptData) {
      setTranscriptData(transcriptData);
      setTranscriptSummary((prevState) => ({
        ...prevState,
        main_topics: transcriptData.summary && transcriptData.summary.main_topics ? transcriptData.summary.main_topics : '',
        inappropriate_violent_themes:
          transcriptData.summary && transcriptData.summary.inappropriate_violent_themes
            ? transcriptData.summary.inappropriate_violent_themes
            : [],
      }));
    }
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackbar(false);
  };

  // V2
  const formatLastRefreshedTime = (timestamp) => {
    if (!timestamp) return '';

    const options = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    };

    return `Last Refreshed ${timestamp.toLocaleTimeString('en-US', options)}`;
  };

  const onPageChange = (page) => {
    setPage(page);
  };

  const onPageSizeChange = (pageSize) => {
    setPage(1);
    setDefaultPageSize(pageSize)
  };

  const onSortChange = (columnName, direction) => {
    const mapping = {
      'LearnerCell': 'learnerUserName',
    }
    setCallHistoryDatasetProps({
      ...callHistoryDatasetProps,
      sort: {
        columnName: mapping[columnName] || columnName,
        direction
      }
    });
  };

  const onFilterChange = (filters) => {
    let tempFilters = {
      learnerFirstName: "",
      learnerLastName: "",
      learnerDocId: "",
      relationshipType: "",
      contactPhoneNumber: "",
      firstName: "",
      lastName: "",
      voiceCallInsights: "",
      contactName: ""
    };
    const mapping = {
      "username": "learnerUserName",
      "firstName": "learnerFirstName",
      "lastName": "learnerLastName",
      "docId": "learnerDocId"
    }
    filters.forEach((filter) => {
      let filterValue = filter.value;

      // Convert labels back to backend values using lookup maps
      if (filter.columnName === "relationshipType") {
        filterValue = filter.value
          .split(",")
          .map(item => relationshipLabelToValue.get(item.trim()) || item.trim())
          .join(",");
        if (filterValue) {
          tempFilters[filter.columnName] = filterValue;
        }
      } else if (filter.columnName === "callState") {
        filterValue = filter.value
          .split(",")
          .map(item => callStateLabelToValue.get(item.trim()) || item.trim())
          .join(",");
        if (filterValue) {
          tempFilters[filter.columnName] = filterValue;
        }
      } else if (filter.columnName === "voiceCallInsights") {
        filterValue = filter.value
          .split(",")
          .map(item => callInsightLabelToValue.get(item.trim()) || item.trim())
          .join(",");
        if (filterValue) {
          tempFilters[filter.columnName] = filterValue;
        }
      }
      else if (filter.columnName === 'endTimeUtc') {
        filterValue = filter.value
        tempFilters[filter.columnName] = filterValue;
      }
      else {
        if (tempFilters[mapping[filter.preDefinedPropName] || filter.columnName]) {
          tempFilters[mapping[filter.preDefinedPropName] || filter.columnName] += "%" + filterValue + "%,";
        } else {
          tempFilters[mapping[filter.preDefinedPropName] || filter.columnName] = "%" + filterValue + "%,";
        }
      }
    });
    setPage(1);
    setCallHistoryDatasetProps({
      ...callHistoryDatasetProps,
      filter: tempFilters,
    });
  };

  const onRefreshHandler = () => {
    getCallHistoryData();
  };

  // V1 MaterialTable columns definition
  let tableColumns = [
    {
      title: 'Resident Name',
      field: 'learnerFirstName',
      filtering: true,
      sorting: true,
      cellStyle: {
        minWidth: 150,
      },
      render: (rowData) => (
        <Hoverable title={rowData.learnerFirstName}>{rowData.learnerFirstName + ' ' + rowData.learnerLastName}</Hoverable>
      ),
    },
    {
      title: 'DOC ID',
      field: 'learnerDocId',
      filtering: true,
      sorting: true,
      cellStyle: {
        minWidth: 100,
      },
      render: (rowData) => <Hoverable title={rowData.learnerDocId}>{rowData.learnerDocId}</Hoverable>,
    },
    {
      title: 'Contact Name',
      field: 'contactName',
      filtering: true,
      sorting: true,
      render: (rowData) => {
        const { firstName, lastName } = JSON.parse(rowData.contactInfo);
        const fullName = lastName ? `${firstName} ${lastName}` : firstName;
        return <Hoverable title={fullName}>{fullName}</Hoverable>;
      },
    },
    {
      title: 'Phone Number',
      field: 'contactPhoneNumber',
      filtering: true,
      sorting: true,
      render: (rowData) => {
        return <Hoverable title={rowData.contactPhoneNumber}>{rowData.contactPhoneNumber}</Hoverable>;
      },
    },
    {
      title: 'Relationship',
      field: 'relationshipType',
      filtering: true,
      sorting: true,
      render: (rowData) => {
        const { relationshipType } = JSON.parse(rowData.contactInfo);
        return <Hoverable title={relationshipType}>{relationshipType}</Hoverable>;
      },
    },
    {
      title: 'Call End Time',
      field: 'endTimeUtc',
      defaultSort: 'desc',
      type: 'date',
      filtering: true,
      sorting: true,
      render: (rowData) => {
        const endTimeUtc = rowData.endTimeUtc;

        if (endTimeUtc === null) {
          return '';
        }
        const timeZone = getSelectedLocationTimeZone();
        const formattedDate = moment.utc(rowData.endTimeUtc).tz(timeZone).format('MM-DD-YYYY hh:mm:ss A');
        return <Hoverable title={formattedDate}>{formattedDate}</Hoverable>;
      },
    },
    {
      title: 'Call Status',
      field: 'callState',
      filtering: true,
      sorting: false,
      lookup: Object.fromEntries(
        Object.values(VOICE_CALL.CALL_HISTORY_STATUS).map(status => [status.value, status.label])
      ),
      render: (rowData) => (
        <Typography className={VOICE_CALL.getCallStatusClassName(rowData.callState, props.classes)}>
          {' '}
          {VOICE_CALL.capitalizeFirstLetter(rowData.callState)}
        </Typography>
      ),
    },
    ...(isKeywordFlagEnabled
      ? [
        {
          title: 'Call Insights',
          field: 'voiceCallInsights',
          filtering: true,
          lookup: Object.fromEntries(
            Object.values(VOICE_CALL.VOICE_INSIGHTS)
              .filter(({ value }) => value !== VOICE_CALL.VOICE_INSIGHTS.VOICE_MATCH_RESIDENT.value)
              .map(({ value, label }) => [value, label])
          ),
          sorting: false,
          render: (rowData) => {
            const { voiceCallInsights } = rowData;

            if (
              !voiceCallInsights ||
              voiceCallInsights.length === 0 ||
              rowData.callState === VOICE_CALL.CALL_HISTORY_STATUS.CANCELLED.value ||
              rowData.callState === VOICE_CALL.CALL_HISTORY_STATUS.MISSED.value
            ) {
              return null; // No insights to display
            }

            // Collect unique events using a Set for event types
            const uniqueInsights = [];
            const seenEventTypes = new Set();

            for (const event of voiceCallInsights) {
              if (!seenEventTypes.has(event.eventType)) {
                uniqueInsights.push(event);
                seenEventTypes.add(event.eventType);
              }
            }

            return (
              <>
                {uniqueInsights
                  .filter(
                    (event) =>
                      VOICE_CALL.VOICE_INSIGHTS[event.eventType] &&
                      VOICE_CALL.VOICE_INSIGHTS[event.eventType].value !==
                      VOICE_CALL.VOICE_INSIGHTS.VOICE_MATCH_RESIDENT.value
                  )
                  .map((event, index) => (
                    <Box key={index}>
                      <VoiceInsightChip
                        label={VOICE_CALL.VOICE_INSIGHTS[event.eventType]?.label}
                        className={rowData?.callType === 'VRS' ? 'vrs' : ''}
                      />
                    </Box>
                  ))}
              </>
            );
          },
        },
      ]
      : []),
    {
      title: 'Actions',
      sorting: false,
      filtering: false,
      render: (rowData) => {
        if (
          [VOICE_CALL.CALL_HISTORY_STATUS.COMPLETED.value, VOICE_CALL.CALL_HISTORY_STATUS.TERMINATED.value].includes(rowData.callState)
        ) {
          return (
            <Button onClick={() => onDetailsClick(rowData)} style={{ fontWeight: 'bold' }}>
              Details
            </Button>
          );
        }
      },
    },
  ];

  // V1 MaterialTable helper functions
  const setTableFilters = (query) => {
    let filters = { ...stateFilters };
    if (!query) return filters;
    if (!query.filters) return filters;
    for (let i = 0; i < query.filters.length; i++) {
      let item = query.filters[i];
      if (allowedFilters.includes(item.column.field)) {
        let value = item.value;
        if (Array.isArray(value)) value = [...item.value];
        filters[item.column.field] = value;
      }
    }
    return filters;
  };

  const createRequestPayload = (query) => {
    const selectedFilters = setTableFilters(query);
    columnSortByOrderDirection(tableColumns, query.orderBy, query.orderDirection);
    const tbody = {
      endTimeUtc: selectedFilters.endTimeUtc ? moment(selectedFilters.endTimeUtc).format('YYYY-MM-DD') : '',
      learnerFirstName: selectedFilters.learnerFirstName,
      learnerDocId: selectedFilters.learnerDocId,
      relationshipType: selectedFilters.relationshipType,
      contactPhoneNumber: selectedFilters.contactPhoneNumber,
      firstName: selectedFilters.contactName,
      lastName: selectedFilters.contactName,
      voiceCallInsights: selectedFilters.voiceCallInsights.join(),
    };
    let callStatus = Object.values(VOICE_CALL.CALL_HISTORY_STATUS).map(s => s.value).join().toLowerCase();
    if (selectedFilters.callState.length > 0) {
      callStatus = selectedFilters.callState.join();
    }
    return { tbody, callStatus };
  };

  const getCallHistoryListData = async (query) => {
    if (!props.locationId) return;
    if (!query) return;
    const { orderBy, orderDirection } = query;
    const { tbody, callStatus } = createRequestPayload(query);
    return new Promise((resolve, reject) => {
      voiceCallService
        .getLiveCallsList({
          locationId: props.locationId,
          status: callStatus,
          pageNumber: query.page,
          size: query.pageSize,
          sortBy: orderBy ? orderBy.field : '',
          sortOrder: orderDirection ? orderDirection : '',
          tbody,
        })
        .then((data) => {
          const { content, pageable, totalElements } = data;
          if (content) {
            resolve({
              data: content,
              page: (pageable && pageable.pageNumber) || 0,
              totalCount: totalElements,
            });
            setCallsData(content);
            setTotalCount(totalElements);
          } else reject(new Error('Something went wrong'));
        })
        .catch((error) => {
          reject(error);
        });
    });
  };

  const exportCallHistoryListData = async () => {
    try {
      if (tableRef && tableRef.current && tableRef.current.state) {
        const { query } = tableRef.current.state;
        const { orderBy, orderDirection } = query;
        const { tbody, callStatus } = createRequestPayload(query);
        const payload = {
          locationId: props.locationId,
          status: callStatus,
          size: totalCount,
          pageNumber: 0,
          tbody,
          sortBy: orderBy ? orderBy.field : '',
          sortOrder: orderDirection ? orderDirection : '',
        };
        const { content } = await voiceCallService.getLiveCallsList(payload);
        if (content) {
          return content;
        }
      }
    } catch (e) {
      // Error in exportCallHistoryListData
    }
  };

  return {
    callHistoryData,
    columns,
    tableColumns,
    callsData,
    isLoading,
    defaultPageSize,
    totalCount,
    isDetailModalOpen,
    callDetails,
    recordingUrl,
    callStatus,
    transcriptData,
    transcriptSummary,
    detectedKeywords,
    lastRefreshed,
    handleCloseHistoryDetailsModal,
    openSnackbar,
    snackbarMessage,
    snackbarAutoHideDuration,
    isSuccessSnackbar,
    handleCloseSnackbar,
    getCallHistoryListData,
    exportCallHistoryListData,
    tableRef,
    handleExportCsv,
    getLocationName,
    getColumnValue,
    isPrivileged,
    formatLastRefreshedTime,
    onPageChange,
    onPageSizeChange,
    onSortChange,
    onFilterChange,
    onRefreshHandler
  };
};
